import React from 'react';
import styled from 'styled-components';

import media from '../../../utils/mediaQueries';
import Btn from '../../buttons/Btn';
import { colors, radius, spacing } from '../tokens';
import {
  Accompagnement, ContactInfo, DemarcheChoice, NoAccompagnementMessage,
} from '../types';
import {
  Actions, ErrorBanner, Panel, StepText, StepTitle,
} from './styles';

const Recap = styled.dl`
  margin: ${spacing.lg} 0 ${spacing.xl};
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.sm} ${spacing.md};
  font-size: 14px;
  line-height: 1.45;

  dt {
    color: ${colors.textMuted};
  }

  dd {
    margin: 0;
    color: ${colors.text};
    font-weight: 600;
  }
`;

const ChoiceIntro = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: ${colors.text};
  margin: 0 0 ${spacing.xs};
`;

const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md};
  margin: ${spacing.lg} 0;

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const ChoiceCard = styled.div<{ isRecommended?: boolean }>`
  position: relative;
  padding: ${spacing.lg};
  border: 1px solid ${({ isRecommended }) => (isRecommended ? colors.brand : colors.border)};
  border-radius: ${radius.medium};
  background-color: ${({ isRecommended }) => (isRecommended ? colors.brandSoft : colors.surface)};
  display: flex;
  flex-direction: column;
`;

const ChoiceBadge = styled.span`
  align-self: flex-start;
  margin-bottom: ${spacing.sm};
  padding: 2px 10px;
  border-radius: 999px;
  background-color: ${colors.brand};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const ChoiceTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.text};
  margin: 0 0 ${spacing.xs};
`;

const ChoicePrice = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${colors.text};
  margin-bottom: ${spacing.xs};
`;

const FreeDiagnosticNote = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.brand};
  margin: 0 0 ${spacing.md};
`;

const ChoiceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${spacing.lg};
  flex: 1;

  li {
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.textMuted};
    padding-left: 20px;
    position: relative;
    margin-bottom: ${spacing.xs};

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: ${colors.brand};
      font-weight: 700;
    }
  }
`;

const Caption = styled.p`
  font-size: 12px;
  color: ${colors.textMuted};
  margin: ${spacing.md} 0 0;
`;

const InfoBlock = styled.div`
  padding: ${spacing.lg};
  border: 1px solid ${colors.border};
  border-radius: ${radius.medium};
  background-color: ${colors.brandSoft};
  margin: ${spacing.lg} 0;
`;

const InfoBlockTitle = styled.div`
  font-weight: 700;
  color: ${colors.text};
  margin-bottom: ${spacing.xs};
`;

const InfoBlockLink = styled.a`
  color: ${colors.brand};
  font-weight: 600;
`;

type SummaryChoiceStepProps = {
  demarcheName: string;
  price: string;
  situationLabel: string;
  contactInfo: ContactInfo;
  accompagnement: Accompagnement | null;
  situationAllowsAccompagnement: boolean;
  noAccompagnementMessage?: NoAccompagnementMessage;
  isChoosingPayant?: boolean;
  payantErrorMessage?: string | null;
  onChoose: (choice: DemarcheChoice) => void;
  onBack: () => void;
};

const SummaryChoiceStep: React.FC<SummaryChoiceStepProps> = ({
  demarcheName,
  price,
  situationLabel,
  contactInfo,
  accompagnement,
  situationAllowsAccompagnement,
  noAccompagnementMessage,
  isChoosingPayant = false,
  payantErrorMessage = null,
  onChoose,
  onBack,
}) => {
  const hasContactInfo = Boolean(contactInfo.prenom || contactInfo.nom);

  return (
    <Panel>
      <StepTitle>Récapitulatif</StepTitle>
      <Recap>
        <dt>Démarche</dt>
        <dd>{demarcheName}</dd>
        <dt>Votre situation</dt>
        <dd>{situationLabel || '—'}</dd>
        {hasContactInfo && (
          <>
            <dt>Contact</dt>
            <dd>{`${contactInfo.prenom} ${contactInfo.nom}`}</dd>
            <dt>E-mail</dt>
            <dd>{contactInfo.email}</dd>
            {contactInfo.telephone && (
              <>
                <dt>Téléphone</dt>
                <dd>{contactInfo.telephone}</dd>
              </>
            )}
          </>
        )}
      </Recap>

      {situationAllowsAccompagnement && accompagnement ? (
        <>
          <ChoiceIntro>Comment souhaitez-vous continuer ?</ChoiceIntro>
          <StepText>
            Les deux options mènent à la même démarche officielle auprès de France Titres — ANTS.
            VroomVroom n’est pas un service de l’État.
          </StepText>
          <ChoiceGrid>
            <ChoiceCard>
              <ChoiceTitle>Le faire moi-même</ChoiceTitle>
              <ChoicePrice>Gratuit</ChoicePrice>
              <ChoiceList>
                <li>Accès direct au service officiel</li>
                <li>Aucun frais VroomVroom</li>
                <li>Vous gérez seul vos documents</li>
              </ChoiceList>
              <Btn
                type="button"
                name="demarche-funnel-choice-gratuit"
                text="Continuer seul"
                outlined
                block
                onClick={() => onChoose('gratuit')}
              />
            </ChoiceCard>
            <ChoiceCard isRecommended>
              <ChoiceBadge>Recommandé</ChoiceBadge>
              <ChoiceTitle>Être accompagné par un pro</ChoiceTitle>
              <ChoicePrice>{`${price} TTC`}</ChoicePrice>
              {accompagnement.freeDiagnostic && (
                <FreeDiagnosticNote>Premier diagnostic gratuit, sans engagement.</FreeDiagnosticNote>
              )}
              <ChoiceList>
                {accompagnement.inclusions.slice(0, 4).map((item) => <li key={item}>{item}</li>)}
              </ChoiceList>
              <Btn
                type="button"
                name="demarche-funnel-choice-payant"
                text="Choisir l’accompagnement"
                block
                isLoading={isChoosingPayant}
                disabled={isChoosingPayant}
                onClick={() => onChoose('payant')}
              />
            </ChoiceCard>
          </ChoiceGrid>
          {payantErrorMessage && <ErrorBanner>{payantErrorMessage}</ErrorBanner>}
          <Caption>
            VroomVroom est un service privé indépendant de France Titres — ANTS. Le recours à
            l’accompagnement ne garantit ni un délai administratif ni l’acceptation de la demande.
          </Caption>
        </>
      ) : (
        <>
          <InfoBlock>
            <InfoBlockTitle>
              {noAccompagnementMessage?.title || 'Pas d’accompagnement payant pour cette démarche'}
            </InfoBlockTitle>
            <StepText>
              {noAccompagnementMessage?.body
                || `VroomVroom ne propose pas d’accompagnement payant pour « ${demarcheName} ». La `
                  + 'démarche officielle reste simple à réaliser vous-même ; nous vous guidons vers '
                  + 'les bonnes ressources.'}
              {noAccompagnementMessage?.linkUrl && (
                <>
                  {' '}
                  <InfoBlockLink href={noAccompagnementMessage.linkUrl} target="_blank" rel="noopener noreferrer">
                    {noAccompagnementMessage.linkLabel || noAccompagnementMessage.linkUrl}
                  </InfoBlockLink>
                </>
              )}
            </StepText>
          </InfoBlock>
          <Btn
            type="button"
            name="demarche-funnel-choice-continue-free"
            text="Continuer vers ma démarche"
            block
            onClick={() => onChoose('gratuit')}
          />
        </>
      )}

      <Actions>
        <Btn
          type="button"
          name="demarche-funnel-summary-back"
          text="Retour"
          outlined
          block
          onClick={onBack}
        />
      </Actions>
    </Panel>
  );
};

export { SummaryChoiceStep };
